/**
 * "New calendar" CTA. Picks a project, period, capacity, and — Phase 1
 * keyword-picker refactor — optionally an explicit keyword-run to seed
 * from. When left on "Auto", the backend defaults to the latest completed
 * run (existing behavior).
 */
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { CalendarDays, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import type { ContentCalendar, Project } from '@seo/types';
import { Button, type ButtonProps } from '@/components/ui/button';
import { FormDialog, FormDialogBlock } from '@/components/ui/form-dialog';
import { TextField, FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractFieldErrors, summarizeApiError } from '@/lib/api';
import { useCreateCalendar } from '@/features/calendars/use-create-calendar';
import { useKeywordRuns } from '@/features/keywords/use-keyword-runs';

// Sentinel for the "Auto — latest completed run" option in the Select.
const AUTO_KEYWORD_RUN = '__auto__';

const CalendarFormSchema = z.object({
  project_id: z.string().uuid({ message: 'Pick a project' }),
  period_months: z
    .number({ invalid_type_error: 'Enter a number' })
    .int()
    .min(1, 'At least 1 month')
    .max(12, 'No more than 12 months'),
  capacity_per_month: z
    .number({ invalid_type_error: 'Enter a number' })
    .int()
    .min(1, 'At least 1 item per month')
    .max(60, 'No more than 60 items per month'),
  /** Empty string = auto (backend picks latest completed). */
  keyword_run_id: z.string().uuid().or(z.literal(AUTO_KEYWORD_RUN)),
});
type CalendarFormValues = z.infer<typeof CalendarFormSchema>;

export interface CalendarCreateButtonProps {
  projects: Project[];
  defaultProjectId?: string;
  label?: string;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  navigateOnSuccess?: boolean;
}

export function CalendarCreateButton({
  projects,
  defaultProjectId,
  label = 'New calendar',
  buttonProps,
  navigateOnSuccess = true,
}: CalendarCreateButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const trigger = (
    <Button {...buttonProps}>
      <CalendarDays className="h-4 w-4" />
      {label}
    </Button>
  );

  if (projects.length === 0) {
    return (
      <FormDialogBlock
        open={open}
        onOpenChange={setOpen}
        trigger={trigger}
        title="No projects yet"
        description="Create a project first — calendars are generated per project."
      />
    );
  }

  return (
    <CalendarDialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      projects={projects}
      defaultProjectId={defaultProjectId}
      navigateOnSuccess={navigateOnSuccess}
    />
  );
}

function CalendarDialog({
  open,
  onOpenChange,
  trigger,
  projects,
  defaultProjectId,
  navigateOnSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trigger: JSX.Element;
  projects: Project[];
  defaultProjectId?: string;
  navigateOnSuccess: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateCalendar();
  const initialProjectId =
    defaultProjectId ?? (projects.length === 1 ? projects[0]?.id : '') ?? '';

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CalendarFormValues>({
    resolver: zodResolver(CalendarFormSchema),
    defaultValues: {
      project_id: initialProjectId,
      period_months: 3,
      capacity_per_month: 8,
      keyword_run_id: AUTO_KEYWORD_RUN,
    },
    mode: 'onBlur',
  });

  const projectId = watch('project_id');
  const period = watch('period_months');
  const capacity = watch('capacity_per_month');
  const keywordRunId = watch('keyword_run_id');

  const runsQuery = useKeywordRuns(projectId || undefined);
  const completedRuns = useMemo(
    () => (runsQuery.data?.items ?? []).filter((r) => r.status === 'completed'),
    [runsQuery.data],
  );

  useEffect(() => {
    if (!projectId && projects.length === 1) {
      setValue('project_id', projects[0]!.id, { shouldValidate: false });
    }
  }, [projectId, projects, setValue]);

  const total =
    Number.isFinite(period) && Number.isFinite(capacity) ? period * capacity : 0;

  const submit = handleSubmit(async (values) => {
    try {
      const explicitRunId =
        values.keyword_run_id && values.keyword_run_id !== AUTO_KEYWORD_RUN
          ? values.keyword_run_id
          : undefined;
      const created = await create.mutateAsync({
        project_id: values.project_id,
        period_months: values.period_months,
        capacity_per_month: values.capacity_per_month,
        ...(explicitRunId ? { keyword_run_id: explicitRunId } : {}),
      });
      toast.success('Calendar queued', {
        description: `${values.period_months}-month plan, ${values.capacity_per_month}/mo`,
      });
      reset();
      onOpenChange(false);
      if (navigateOnSuccess) {
        await navigate({
          to: '/calendars/$calendarId',
          params: { calendarId: (created as ContentCalendar).id },
        });
      }
    } catch (e) {
      const fieldErrors = extractFieldErrors(e);
      for (const f of ['project_id', 'period_months', 'capacity_per_month'] as const) {
        if (fieldErrors[f]) setError(f, { type: 'server', message: fieldErrors[f] });
      }
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not generate calendar',
        fieldLabels: {
          project_id: 'Project',
          period_months: 'Period',
          capacity_per_month: 'Items per month',
        },
      });
      if (summary.code === 'NOT_FOUND' && !fieldErrors.project_id) {
        setError('project_id', { type: 'server', message: summary.description });
      }
      toast.error(summary.title, { description: summary.description });
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      icon={<Sparkles className="h-5 w-5 text-primary" />}
      title="Generate a content calendar"
      description="We'll read the latest audit and keyword run on this project, then propose a pillar + spoke + refresh calendar."
      submitLabel="Generate"
      submitting={isSubmitting || create.isPending}
      onSubmit={submit}
    >
      <FormField label="Project" error={errors.project_id?.message}>
        <Select
          value={projectId}
          onValueChange={(v) => setValue('project_id', v, { shouldValidate: true })}
        >
          <SelectTrigger aria-invalid={errors.project_id ? true : undefined}>
            <SelectValue placeholder="Pick a project" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <div className="grid grid-cols-2 gap-3">
        <TextField
          label="Period (months)"
          type="number"
          min={1}
          max={12}
          error={errors.period_months?.message}
          {...register('period_months', { valueAsNumber: true })}
        />
        <TextField
          label="Items per month"
          type="number"
          min={1}
          max={60}
          error={errors.capacity_per_month?.message}
          {...register('capacity_per_month', { valueAsNumber: true })}
        />
      </div>

      <FormField
        label="Keyword research"
        hint={
          completedRuns.length === 0
            ? 'No completed keyword runs yet — the calendar will fall back to a domain-only seed prompt.'
            : 'Pick a specific run, or let us use the latest completed one.'
        }
      >
        <Select
          value={keywordRunId ?? AUTO_KEYWORD_RUN}
          onValueChange={(v) => setValue('keyword_run_id', v, { shouldValidate: true })}
          disabled={!projectId || runsQuery.isLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder="Auto — use latest completed run" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AUTO_KEYWORD_RUN}>Auto — use latest completed run</SelectItem>
            {completedRuns.map((run) => (
              <SelectItem key={run.id} value={run.id}>
                {describeRun(run)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <div className="rounded-md border border-border/60 bg-muted/40 p-3 text-xs text-muted-foreground">
        We'll plan up to <span className="font-semibold text-foreground">{total}</span>{' '}
        items across{' '}
        <span className="font-semibold text-foreground">
          {period} month{period === 1 ? '' : 's'}
        </span>
        .
      </div>
    </FormDialog>
  );
}

/**
 * Human label for a keyword run in the picker: seed summary + creation date.
 */
function describeRun(run: {
  seed: { type: string; value: unknown };
  created_at: string;
}): string {
  const seed = run.seed;
  let seedText = 'Run';
  if (seed.type === 'domain' && typeof seed.value === 'string') {
    seedText = seed.value.replace(/^https?:\/\//, '');
  } else if (seed.type === 'topics' && Array.isArray(seed.value)) {
    seedText = (seed.value as string[]).slice(0, 2).join(', ');
  } else if (seed.type === 'description' && typeof seed.value === 'string') {
    seedText = seed.value.slice(0, 40) + (seed.value.length > 40 ? '…' : '');
  }
  const date = new Date(run.created_at).toLocaleDateString();
  return `${seedText} · ${date}`;
}
